import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../context/AuthContext";

const tokenCfg = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("bj_admin_token")}` },
});

// Global 401 interceptor — if the token has expired, clear it and bounce to /login
let interceptorInstalled = false;
const installAuthInterceptor = () => {
  if (interceptorInstalled) return;
  interceptorInstalled = true;
  axios.interceptors.response.use(
    (r) => r,
    (err) => {
      const status = err?.response?.status;
      const url = err?.config?.url || "";
      // Only act on protected Airtable/me calls — not on /auth/login itself
      if (status === 401 && !url.endsWith("/auth/login")) {
        localStorage.removeItem("bj_admin_token");
        if (typeof window !== "undefined" && window.location.pathname !== "/login") {
          window.location.replace("/login");
        }
      }
      return Promise.reject(err);
    }
  );
};
installAuthInterceptor();

export const useAirtable = (slug) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/airtable/${slug}`, tokenCfg());
      setRecords(data.records || []);
      setError(null);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : e.message);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { records, loading, error, refetch };
};

export const useOverview = (month) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      const url = month ? `${API}/airtable/overview/stats?month=${month}` : `${API}/airtable/overview/stats`;
      const { data } = await axios.get(url, tokenCfg());
      setData(data);
      setError(null);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : e.message);
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { data, loading, error, refetch };
};

export const updateAirtable = (slug, recordId, fields) =>
  axios
    .patch(`${API}/airtable/${slug}/${recordId}`, { fields }, tokenCfg())
    .then((r) => r.data);

export const createAirtable = (slug, fields) =>
  axios
    .post(`${API}/airtable/${slug}`, { fields }, tokenCfg())
    .then((r) => r.data);

export const useEventsPage = ({ eventType, month, pageSize = 50 }) => {
  const [state, setState] = useState({ records: [], loading: true, error: null, nextOffset: null });
  const [cursors, setCursors] = useState([null]);
  const offset = cursors[cursors.length - 1];

  useEffect(() => { setCursors([null]); }, [eventType, month]);

  useEffect(() => {
    let live = true;
    setState((s) => ({ ...s, loading: true }));
    const params = { page_size: pageSize };
    if (offset) params.offset = offset;
    if (eventType) params.event_type = eventType;
    if (month) params.month = month;
    axios.get(`${API}/events/page`, { ...tokenCfg(), params })
      .then(({ data }) => live && setState({ records: data.records || [], loading: false, error: null, nextOffset: data.next_offset || null }))
      .catch((e) => {
        if (!live) return;
        const detail = e?.response?.data?.detail;
        setState({ records: [], loading: false, error: typeof detail === "string" ? detail : e.message, nextOffset: null });
      });
    return () => { live = false; };
  }, [offset, eventType, month, pageSize]);

  return {
    ...state,
    page: cursors.length,
    next: () => state.nextOffset && setCursors((c) => [...c, state.nextOffset]),
    prev: () => setCursors((c) => (c.length > 1 ? c.slice(0, -1) : c)),
    reset: () => setCursors([null]),
  };
};

export const fetchEventTypes = () => axios.get(`${API}/events/types`, tokenCfg()).then((r) => r.data);

export const fetchAllRecords = (slug) => axios.get(`${API}/airtable/${slug}`, tokenCfg()).then((r) => r.data.records || []);
